import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiDownload, FiExternalLink, FiFileText, FiImage } from 'react-icons/fi';

/**
 * Universal preview modal for PDF and images.
 * Uses React Portal to mount on document.body, ensuring it renders
 * full-screen over the entire viewport rather than trapped inside any parent container.
 */
export default function DocumentPreviewModal({
  open,
  onClose,
  title = 'Document Preview',
  fileName,
  url,
  contentType = 'application/pdf',
  onDownload,
}) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open || !url) return null;

  const isImage =
    Boolean(contentType?.startsWith('image/')) ||
    /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(fileName || '');

  const modalContent = (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-950/75 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 99999,
      }}
    >
      <div
        className="bg-white w-full max-w-6xl h-[92vh] max-h-[94vh] rounded-2xl border border-sky-100 shadow-2xl overflow-hidden flex flex-col animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with light blue accent */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-sky-100 bg-sky-50/70 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center shrink-0">
              {isImage ? <FiImage size={16} /> : <FiFileText size={16} />}
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-slate-900 truncate">{title}</h3>
              {fileName && (
                <p className="text-xs font-mono text-slate-500 truncate">{fileName}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {onDownload && (
              <button
                type="button"
                onClick={onDownload}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-sky-700 bg-white hover:bg-sky-100/70 border border-sky-200 rounded-lg transition-colors"
                title="Download file"
              >
                <FiDownload size={13} />
                <span className="hidden sm:inline">Download</span>
              </button>
            )}

            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors"
              title="Open in new tab"
            >
              <FiExternalLink size={13} />
              <span className="hidden sm:inline">Open in Tab</span>
            </a>

            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 transition-colors ml-1"
              title="Close (Esc)"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Content Viewer */}
        <div className="flex-1 overflow-auto p-4 bg-slate-50 flex items-center justify-center min-h-[300px]">
          {isImage ? (
            <div className="max-w-full max-h-full flex items-center justify-center p-2">
              <img
                src={url}
                alt={title || fileName}
                className="max-h-[80vh] max-w-full object-contain rounded-lg shadow-sm border border-slate-200 bg-white"
              />
            </div>
          ) : (
            <iframe
              src={url}
              title={title || fileName}
              className="w-full h-full rounded-lg border border-slate-200 bg-white shadow-sm min-h-[500px]"
            />
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent;
}
